// UC25 — fișa agregării: the definition read back in the administrator's words,
// the rows it computed, and what would break if it went away.
// per wireframes/react/src/pages/AdminAgregari.tsx (fișa, secțiunile Detalii /
//   Date / Legături / Istoric, și meniul de acțiuni din antet la :1487-1500)
// per docs/caiet-uc-spec.md §UC25-01 §UC-AGG-06 §UC-AGG-07 §UC-AGG-08
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import {
  MudButton,
  MudInfoBox,
  MudSelect,
  MudTab,
  MudTable,
  MudTabs,
  MudTag,
} from '@egov-moldova/mud-react';
import {
  Breadcrumb,
  EMPTY_CELL,
  EmptyRegisterMessage,
  EmptyState,
  FilterBar,
  dateInputToIso,
  isoToDateInput,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  formatDate,
  formatDateTime,
  formatNumber,
  formatTime,
  type ActiveFilter,
  DraftDateInput,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { HistoryActor } from '../../features/history/CatalogHistoryLog';
import { HistoryMeta } from '../../features/history/HistoryMeta';
import { FormRow, FormSection, FormValue } from '../../components/FormPage/FormPage';
import { loadFailureKind } from '../../app/api-client';
import { statusSemantic } from './AggregatesPage';
import {
  carriesLevel,
  getAggregate,
  getAggregateHistory,
  getAggregateRows,
  getDependencies,
  isExpression,
  LEVEL_KEY,
  isPresetRetention,
  isPresetSchedule,
  hasRecordedActor,
  splitInterval,
  type AggregateDependency,
  type AggregateHistoryEntry,
  type AggregateView,
  type Calculation,
} from './aggregates-domain';
import { useAggregateActions } from './useAggregateActions';
import { HISTORY_PERIODS, isPeriodActive, periodRange } from './history-period';
import './aggregates.css';
import { TableFooter, usePageSize } from '../../components/TableFooter/TableFooter';

// The wireframe's four sections (AdminAgregari.tsx:95-99). "istoric" is the one
// the application was missing at every layer — no tab, no route, no endpoint and
// no table — which left UC-AGG-07's recalculations and UC-AGG-08's manual
// refresh/rebuild with no read-back at all.
const TABS = ['details', 'data', 'links', 'history'] as const;
type Tab = (typeof TABS)[number];

// The column core publishes for a row's expiry class (sql-template.ts).
const AVAILABLE_UNTIL = 'available_until';

function readTab(raw: string | null): Tab {
  return TABS.find((tab) => tab === raw) ?? 'details';
}

// A schedule or a retention the administrator did not pick from the list is
// rendered from its parts rather than as the raw PostgreSQL interval.
function useIntervalLabel(): (value: string, kind: 'schedule' | 'retention') => string {
  const { t } = useTranslate();
  return (value, kind) => {
    if (!value) return t('aggregates.retention.permanent');
    if (kind === 'schedule' && isPresetSchedule(value)) return t(`aggregates.schedule.${value}`);
    if (kind === 'retention' && isPresetRetention(value)) return t(`aggregates.retention.${value}`);
    const parts = splitInterval(value);
    if (!parts) return value;
    return kind === 'schedule'
      ? t('aggregates.schedule.custom_label', {
          amount: parts.amount,
          unit: t(`aggregates.unit.${parts.unit}`),
        })
      : t('aggregates.retention.custom_label', {
          amount: parts.amount,
          unit: t(`aggregates.unit.${parts.unit}`),
        });
  };
}

// One calculated result, as the wireframe reads it back: a row of pairs, not a
// card (AdminAgregari.tsx:1552 — `dl.kv.c3`, three columns of dt/dd).
//
// The record had grown a bordered, tinted box per result with an uppercase
// label and a "Rezultat n / Funcție" tag pair on top, which is the EDITOR's
// shape (the editor needs a frame because each result owns controls and a
// remove button). Reading one back needs none of that: three facts, in the same
// bands and the same type as every other pair on the page. Eight results drew
// eight nested boxes inside an already-bordered section.
//
// The alias keeps `<code>` — it is the identifier the operator types into an
// alert rule, so it is quoted material rather than prose.
function CalculationValues({ calculation }: { calculation: Calculation }): JSX.Element {
  const { t } = useTranslate();
  return (
    <FormRow columns={3} as="dl">
      <FormValue label={t('aggregates.form.result_name')}>
        <code>{calculation.alias}</code>
      </FormValue>
      {isExpression(calculation) ? (
        <>
          <FormValue label={t('aggregates.form.expression_template')}>
            {t(`aggregates.expression.${calculation.template}`)}
          </FormValue>
          <FormValue label={t('aggregates.form.first_operand')}>
            <code>{calculation.firstOperand}</code>
          </FormValue>
          {/* The wireframe draws this pair only when the template takes two
              operands (AdminAgregari.tsx:1573); "Procent din total" has one, and
              an empty "Compară cu" would read as a value that failed to load. */}
          {calculation.secondOperand ? (
            <FormValue label={t('aggregates.form.second_operand')}>
              <code>{calculation.secondOperand}</code>
            </FormValue>
          ) : null}
        </>
      ) : (
        <>
          <FormValue label={t('aggregates.form.function')}>
            {t(`aggregates.function.${calculation.fn}`)}
          </FormValue>
          <FormValue label={t('aggregates.form.measure')}>
            {t(`aggregates.measure.${calculation.column}`, calculation.column)}
          </FormValue>
        </>
      )}
    </FormRow>
  );
}

// The wireframe's `tbl compact admin-relations-table` (AdminAgregari.tsx
// :1697-1703) as AGE's own table. Every cell is slotted, so mud-table's nowrap
// `.cell-text` never ellipsises a name or the usage sentence.
function DependencyTable({ dependencies }: { dependencies: AggregateDependency[] }): JSX.Element {
  const { t } = useTranslate();
  const columns: TableColumn[] = [
    { key: 'element', label: t('aggregates.links.column.element') },
    { key: 'kind', label: t('aggregates.links.column.kind'), width: '150px' },
    { key: 'usage', label: t('aggregates.links.column.usage') },
    { key: 'status', label: t('aggregates.links.column.status'), width: '130px' },
  ];
  const rows: TableRowData[] = dependencies.map((dependency) => ({
    id: `${dependency.kind}-${dependency.ref}`,
  }));
  return (
    <div className="simsm-aggregate__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={rows}
        rowStyle="divided"
        disableSort={true}
        aria-label={t('aggregates.links.title')}
      >
        {dependencies.map((dependency, index) => {
          const id = `${dependency.kind}-${dependency.ref}`;
          return [
            <span
              key={`element-${id}`}
              slot={`cell-element-${index}`}
              className="simsm-aggregate__cell"
            >
              {dependency.kind === 'aggregate' ? (
                <RouterLink
                  to={`/system-admin/aggregates/${dependency.ref}`}
                  size="sm"
                  variant="strict"
                  underline="hover"
                >
                  {dependency.label}
                </RouterLink>
              ) : dependency.kind === 'stock_view' ? (
                <RouterLink to="/system-admin/stocks" size="sm" variant="strict" underline="hover">
                  {dependency.label}
                </RouterLink>
              ) : (
                dependency.label
              )}
            </span>,
            <span key={`kind-${id}`} slot={`cell-kind-${index}`} className="simsm-aggregate__cell">
              <MudTag
                variant="status"
                type="subtle"
                semantic="neutral"
                label={t(`aggregates.dependency.kind.${dependency.kind}`)}
              />
            </span>,
            <span
              key={`usage-${id}`}
              slot={`cell-usage-${index}`}
              className="simsm-aggregate__cell"
            >
              {t(`aggregates.dependency.usage.${dependency.kind}`)}
            </span>,
            <span
              key={`status-${id}`}
              slot={`cell-status-${index}`}
              className="simsm-aggregate__cell"
            >
              <MudTag
                variant="status"
                type={dependency.active ? 'outlined' : 'subtle'}
                semantic={dependency.active ? 'success' : 'neutral'}
                label={
                  dependency.active
                    ? t('aggregates.status.active')
                    : t('aggregates.status.archived')
                }
              />
            </span>,
          ];
        })}
      </MudTable>
    </div>
  );
}

// caiet UC-AGG-07 / UC-AGG-08, wireframe AdminAgregari.tsx:1774-1782 — the five
// columns the wireframe draws, `tbl zebra compact`: Când / Eveniment / Interval /
// Statut / Efectuat de, as AGE's own table, at the wireframe's own widths.
function HistoryTable({ entries }: { entries: AggregateHistoryEntry[] }): JSX.Element {
  const { t } = useTranslate();
  const columns: TableColumn[] = [
    { key: 'when', label: t('aggregates.history.column.when'), width: '150px' },
    { key: 'event', label: t('aggregates.history.column.event'), width: '190px' },
    { key: 'range', label: t('aggregates.history.column.range') },
    { key: 'status', label: t('aggregates.history.column.status'), width: '120px' },
    { key: 'actor', label: t('aggregates.history.column.actor'), width: '170px' },
  ];
  const rows: TableRowData[] = entries.map((entry) => ({ id: entry.id }));
  return (
    <div className="simsm-aggregate__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={rows}
        rowStyle="zebra"
        disableSort={true}
        aria-label={t('aggregates.history.title')}
      >
        {entries.map((entry, index) => [
          // The day on the first line, the clock beneath it as a labelled pair —
          // the owner's ruling on the geography history ("Când … Ziua / Ora, una
          // sub alta"), which the wireframe writes with the same MetadataLine
          // this page's sibling logs use (AdminAgregari.tsx:1785-1787). One
          // `formatDateTime` line made a 150px column wrap mid-timestamp.
          <span
            key={`when-${entry.id}`}
            slot={`cell-when-${index}`}
            className="simsm-aggregate__cell simsm-aggregate__cell--stack"
          >
            <span className="simsm-table__keep">{formatDate(entry.recordedAt)}</span>
            <HistoryMeta
              label={t('history.meta.label.time')}
              value={formatTime(entry.recordedAt)}
            />
          </span>,
          <span
            key={`event-${entry.id}`}
            slot={`cell-event-${index}`}
            className="simsm-aggregate__cell"
          >
            {t(`aggregates.history.event.${entry.event}`)}
          </span>,
          <span
            key={`range-${entry.id}`}
            slot={`cell-range-${index}`}
            className="simsm-aggregate__cell simsm-aggregate__cell--stack"
          >
            <span className="simsm-table__wrap">
              {entry.rangeStart || entry.rangeEnd
                ? t('aggregates.history.range.between', {
                    from: entry.rangeStart
                      ? formatDate(entry.rangeStart)
                      : t('aggregates.history.range.open'),
                    to: entry.rangeEnd
                      ? formatDate(entry.rangeEnd)
                      : t('aggregates.history.range.open'),
                  })
                : t('aggregates.history.range.all')}
            </span>
            {entry.detail ? (
              <span className="simsm-aggregate__cell-sub">{entry.detail}</span>
            ) : null}
          </span>,
          // Two states, because two is all the system records: a history row is
          // written when an operation finishes, and `AggregateHistoryStatus` is
          // `'SUCCEEDED' | 'FAILED'` (aggregate-history.entity.ts:23). The
          // wireframe draws a third, "Se procesează", in MUD's `accent` — but
          // that row is one it invents client-side from its own `busy` flag and
          // never persists, and the register already shows a run in flight as a
          // spinner. Rendering an accent branch here would be dead code standing
          // for a state nothing can produce. If an in-progress row is ever
          // stored, `accent` is the style it takes: MUD publishes six and
          // Warning is not among them (mud/components/tag.md — "For a cautionary
          // status use Accent … do not invent an amber tag").
          <span
            key={`status-${entry.id}`}
            slot={`cell-status-${index}`}
            className="simsm-aggregate__cell"
          >
            <MudTag
              variant="status"
              type={entry.status === 'SUCCEEDED' ? 'outlined' : 'subtle'}
              semantic={entry.status === 'SUCCEEDED' ? 'success' : 'danger'}
              label={t(`aggregates.history.status.${entry.status}`)}
            />
          </span>,
          // caiet UC10: SIMSM keeps no user directory, so the actor is the masked
          // IDNP the server sends, else the role, through the same renderer as
          // every other history (CF-63, SEC-14: no name is stored). No actor at
          // all is the system's own schedule, which this cell names as the system
          // — `hasRecordedActor` holds that, and `aggregates-domain.spec.ts`
          // holds `hasRecordedActor`.
          //
          // The role is not written beside the id: the owner's ruling on the
          // geography history was "scoate Rol" — this cell answers who, and the
          // person or the system is the whole of that answer.
          <span
            key={`actor-${entry.id}`}
            slot={`cell-actor-${index}`}
            className="simsm-aggregate__cell"
          >
            {hasRecordedActor(entry) ? (
              <HistoryActor entry={entry} />
            ) : (
              t('aggregates.history.actor.system')
            )}
          </span>,
        ])}
      </MudTable>
    </div>
  );
}

export function AggregateDetailPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { name = '' } = useParams<{ name: string }>();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [rowsPageSize, setRowsPageSize] = usePageSize('aggregate-rows');
  const [historyPageSize, setHistoryPageSize] = usePageSize('aggregate-history');
  const tab = readTab(params.get('tab'));
  const intervalLabel = useIntervalLabel();

  // The Istoric section's period filter, in the address: a trail narrowed to a
  // week is a thing an administrator shares and returns to, and the tab is
  // already there. `custom` carries its own two days; every other preset
  // computes them.
  const period = params.get('period') ?? '';
  const customFrom = params.get('from') ?? '';
  const customTo = params.get('to') ?? '';
  const historyRange = periodRange(period, customFrom, customTo);

  // One writer for the address, so a tab switch keeps the filter and a filter
  // change keeps the tab. Changing a criterion sends the reader back to the
  // first page: page 4 of the unfiltered trail is rarely page 4 of the
  // filtered one, and an empty page reads as an empty history.
  function setParam(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'tab') setHistoryPage(1);
    setParams(next, { replace: true });
  }

  function resetPeriod(): void {
    const next = new URLSearchParams(params);
    for (const key of ['period', 'from', 'to']) next.delete(key);
    setHistoryPage(1);
    setParams(next, { replace: true });
  }

  const detail = useQuery({
    queryKey: ['admin-aggregate', name],
    queryFn: () => getAggregate(name),
    enabled: name.length > 0,
    retry: false,
  });

  const dependencies = useQuery({
    queryKey: ['admin-aggregate-dependencies', name],
    queryFn: () => getDependencies(name),
    enabled: name.length > 0 && tab === 'links',
  });

  const rows = useQuery({
    queryKey: ['admin-aggregate-rows', name, page, rowsPageSize],
    queryFn: () => getAggregateRows(name, rowsPageSize, (page - 1) * rowsPageSize),
    enabled: name.length > 0 && tab === 'data',
  });

  const history = useQuery({
    queryKey: [
      'admin-aggregate-history',
      name,
      historyPage,
      historyPageSize,
      historyRange.from,
      historyRange.to,
    ],
    queryFn: () =>
      getAggregateHistory(name, historyPageSize, (historyPage - 1) * historyPageSize, historyRange),
    enabled: name.length > 0 && tab === 'history',
  });

  // The wireframe's header carries "Editează" plus the same action menu the
  // register's rows carry, minus those two entries (AdminAgregari.tsx:1487-1500).
  // A delete from here has nowhere to come back to, so it returns to the register.
  const { actions, dialogs } = useAggregateActions({
    omit: ['open', 'edit'],
    onDeleted: () => navigate('/system-admin/aggregates'),
  });

  if (detail.isError) {
    // The body already told the truth; the headline did not. It announced the
    // aggregate "not found" over a body that said only that the data could not
    // be loaded, and the headline is the sentence a reader takes away.
    const kind = loadFailureKind(detail.error);
    return (
      <section className="simsm-page simsm-aggregate">
        <MudInfoBox
          emphasis="strong"
          variant="warning"
          titleText={
            kind === 'not_found'
              ? t('aggregates.detail.missing.title')
              : kind === 'forbidden'
                ? t('common.forbidden')
                : t('common.load_error')
          }
        >
          {kind === 'not_found' ? t('aggregates.detail.missing.body', { name }) : ''}
        </MudInfoBox>
        <div className="simsm-aggregate__back">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => navigate('/system-admin/aggregates')}
          >
            {t('aggregates.detail.back')}
          </MudButton>
        </div>
      </section>
    );
  }

  const aggregate: AggregateView | undefined = detail.data;
  if (!aggregate) {
    return (
      <p className="simsm-query-state__loading" role="status" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }

  // The chip the wireframe's `chipPerioada` builds: the preset's own label, or
  // the two days of a custom interval. A custom period with neither day entered
  // narrows nothing and is therefore not yet a criterion.
  const activePeriodFilter: ActiveFilter[] = isPeriodActive(period, customFrom, customTo)
    ? [
        {
          key: 'period',
          label: t('alerts.filter.period'),
          value:
            period === 'custom'
              ? t('aggregates.history.filter.custom_range', {
                  from: customFrom ? formatDate(customFrom) : '…',
                  to: customTo ? formatDate(customTo) : '…',
                })
              : t(`alerts.filter.period.${period}`),
          onRemove: resetPeriod,
        },
      ]
    : [];

  const rowsPage = rows.data;
  const totalRows = rowsPage?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPageSize));
  const historyTotal = history.data?.total ?? 0;
  const historyPages = Math.max(1, Math.ceil(historyTotal / historyPageSize));
  // The data table's columns come from the DEFINITION, not from whatever keys
  // the first row happened to carry: the period, then the dimensions grouped on,
  // then one column per calculated result — which is the order the wireframe
  // draws and the order the operator built the aggregate in
  // (AdminAgregari.tsx:1618-1632). Reading them off `Object.keys` put them in
  // the view's own column order and, on an empty page, produced no columns at
  // all, so a filtered-to-nothing table lost its head.
  //
  // The split also says which columns are numbers. MUD types a numeric cell
  // separately from a text one ("Number is a distinct type from Text — numeric
  // cells are right-aligned/tabular by design", mud/components/table.md), and
  // AGE spells that `align: 'end'` on the column. The wireframe does the same
  // with `th.n`/`td.n`. Every result is a number; no dimension is.
  //
  // An aggregate that leaves expired lots out keeps each row's expiry class, the
  // last day its lots are available, so two rows of one period and medicine are
  // two classes, not a duplicate (docs/shape-ops-available-stock-expiry.md
  // Decision 5). Over the movements the definition says so; over another
  // aggregate the class comes from the base, which only its rows show.
  const resultAliases = aggregate.calculations.map((calculation) => calculation.alias);
  // Q-1-05: the results stored are net movements; a stock aggregate's rows also
  // state the stock at the end of the interval, after the results, so the register
  // and the stock screen show one figure (CF-41).
  const showsLevel = carriesLevel(rowsPage?.rows ?? []);
  const isResultColumn = (key: string): boolean =>
    resultAliases.includes(key) || (showsLevel && key === LEVEL_KEY);
  const carriesAvailability =
    (aggregate.source === 'stock_movements' && aggregate.excludeExpiredLots) ||
    (rowsPage?.rows ?? []).some((row) => AVAILABLE_UNTIL in row);
  const dataColumns: TableColumn[] = [
    { key: 'bucket', label: t('aggregates.dimension.bucket') },
    ...aggregate.groupBy.map((dimension) => ({
      key: dimension,
      label: t(`aggregates.dimension.${dimension}`, dimension),
    })),
    ...(carriesAvailability && !aggregate.groupBy.includes(AVAILABLE_UNTIL)
      ? [{ key: AVAILABLE_UNTIL, label: t('aggregates.dimension.available_until') }]
      : []),
    ...resultAliases.map((alias) => ({ key: alias, label: alias, align: 'end' as const })),
    ...(showsLevel
      ? [{ key: LEVEL_KEY, label: t('aggregates.data.level'), align: 'end' as const }]
      : []),
  ];

  return (
    <section className="simsm-page simsm-aggregate simsm-aggregate--detail">
      {/* The wireframe's own trail (AdminAgregari.tsx:1462-1476): on Detalii
          the aggregate is the current page; on any other section it becomes a
          link and the section is the current page. All four tabs rendered the
          same two crumbs, so the trail said the reader was somewhere they had
          already left. */}
      <Breadcrumb
        maxVisible={4}
        label={t('common.breadcrumb.aria_label')}
        crumbs={[
          { label: t('aggregates.title'), to: '/system-admin/aggregates' },
          { label: aggregate.displayName, to: `/system-admin/aggregates/${aggregate.name}` },
          ...(tab === 'details' ? [] : [{ label: t(`aggregates.detail.tab.${tab}`) }]),
        ]}
      />

      {/* The state belongs to the name, not to the button row: the wireframe
          writes it inside the H1 (`.admin-title-status`, AdminAgregari.tsx:1477).
          In the actions cluster it sat between "Editează" and the overflow menu
          and read as a third control. */}
      <PageHeader
        title={
          <span className="simsm-page-header__title-status">
            {aggregate.displayName}
            <MudTag
              variant="status"
              type={aggregate.status === 'active' ? 'outlined' : 'subtle'}
              semantic={statusSemantic(aggregate.status)}
              label={t(`aggregates.status.${aggregate.status}`)}
            />
          </span>
        }
        lede={aggregate.description ?? undefined}
        actions={
          <>
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              onClick={() => navigate(`/system-admin/aggregates/${aggregate.name}/edit`)}
            >
              {t('aggregates.action.edit')}
            </MudButton>
            {/* The wireframe puts refresh-on-range, full rebuild, activate /
                deactivate and delete here beside the edit action, so the detail
                view is not a dead end that sends the administrator back to the
                register to act (AdminAgregari.tsx:1493-1500). */}
            <RowActionsMenu
              size="md"
              label={t('aggregates.action.row_menu', { name: aggregate.displayName })}
              actions={actions(aggregate)}
            />
          </>
        }
      />

      {aggregate.lastError ? (
        <MudInfoBox
          emphasis="strong"
          variant="warning"
          titleText={t('aggregates.detail.last_error.title')}
        >
          {aggregate.lastError}
        </MudInfoBox>
      ) : null}

      {/* The tab strip spans the page, as on the Registrar file: three of the
          four tabs below are full width (owner ruling, 2026-09-12), and a strip
          stopping at 896 over a 1200 table reads as two pages. */}
      <div>
        {/* `sm`, as on every other tab strip here (Entități raportoare, Alerte,
            Dashboard-uri) and as the wireframe's `.tab` draws it: 14/20 in the
            body face (mud.css:77, :1655). MUD's default `md` sets 16/24. */}
        <MudTabs
          size="sm"
          value={tab}
          aria-label={t('aggregates.detail.tabs_aria')}
          onMudChange={(e: CustomEvent<{ value: string }>) =>
            setParam('tab', readTab(e.detail.value))
          }
        >
          <MudTab size="sm" value="details" label={t('aggregates.detail.tab.details')} />
          <MudTab size="sm" value="data" label={t('aggregates.detail.tab.data')} />
          <MudTab size="sm" value="links" label={t('aggregates.detail.tab.links')} />
          <MudTab size="sm" value="history" label={t('aggregates.detail.tab.history')} />
        </MudTabs>
      </div>

      <div
        className={
          tab === 'details' ? 'simsm-aggregate__panel simsm-page__group' : 'simsm-aggregate__panel'
        }
      >
        {/* The definition read back in the EDITOR's own layout — the same
            `config-sect` bands, the same rows, the same order
            (AdminAgregari.tsx:1515-1610 renders the fișa's Detalii with the very
            markup its editor uses). It was a stack of bordered cards with
            uppercase `dt` labels, so a field sat somewhere different depending
            on whether you were reading it or changing it, and none of the four
            other admin records look like that. */}
        {tab === 'details' ? (
          <>
            <FormSection
              title={t('aggregates.section.calculation')}
              lede={t('aggregates.section.calculation_lede')}
            >
              <FormRow columns={2} as="dl">
                <FormValue label={t('aggregates.form.source')}>
                  {t(`aggregates.source.${aggregate.source}`)}
                </FormValue>
                {aggregate.baseAggregate ? (
                  <FormValue label={t('aggregates.form.base')}>
                    <RouterLink
                      to={`/system-admin/aggregates/${aggregate.baseAggregate}`}
                      size="sm"
                      variant="strict"
                      underline="hover"
                    >
                      {aggregate.baseAggregate}
                    </RouterLink>
                  </FormValue>
                ) : null}
                <FormValue label={t('aggregates.form.bucket')}>
                  {t(`aggregates.bucket.${aggregate.bucketInterval}`)}
                </FormValue>
                <FormValue label={t('aggregates.detail.expired_lots')}>
                  {aggregate.excludeExpiredLots
                    ? t('aggregates.detail.expired_lots.excluded')
                    : t('aggregates.detail.expired_lots.included')}
                </FormValue>
              </FormRow>

              {/* A pair like any other, so the dimensions line up with the
                  fields above them; the value happens to be a row of tags.
                  MUD sets their pitch at 8px (mud/components/tag.md, verbatim:
                  "The spacing between horizontally stacked tags should be set to
                  8px"), which is why this does not take the wireframe's 4px
                  `.etichete` gap. */}
              <FormRow as="dl">
                <FormValue label={t('aggregates.form.dimensions')}>
                  <span className="simsm-aggregate__chips">
                    {aggregate.groupBy.map((dimension) => (
                      <MudTag
                        key={dimension}
                        variant="status"
                        type="subtle"
                        semantic="neutral"
                        label={t(`aggregates.dimension.${dimension}`, dimension)}
                      />
                    ))}
                  </span>
                </FormValue>
              </FormRow>

              {aggregate.calculations.map((calculation) => (
                <CalculationValues key={calculation.alias} calculation={calculation} />
              ))}
            </FormSection>

            <FormSection
              title={t('aggregates.section.schedule')}
              lede={t('aggregates.section.schedule_lede')}
            >
              <FormRow columns={2} as="dl">
                <FormValue label={t('aggregates.form.schedule')}>
                  {aggregate.scheduled
                    ? intervalLabel(aggregate.refreshScheduleInterval, 'schedule')
                    : t('aggregates.schedule.manual')}
                </FormValue>
                <FormValue label={t('aggregates.form.retention')}>
                  {intervalLabel(aggregate.retentionInterval ?? '', 'retention')}
                </FormValue>
                {/* Not in the wireframe's two-pair band, and kept anyway: it is
                    the one fact on this page that changes without anyone editing
                    the definition, and the reader arriving from a failed refresh
                    is here to see it. */}
                <FormValue label={t('aggregates.column.last_refresh')}>
                  {aggregate.lastRefreshAt ? (
                    formatDateTime(aggregate.lastRefreshAt)
                  ) : (
                    <span className="simsm-aggregate__muted">{t('aggregates.state.never')}</span>
                  )}
                </FormValue>
              </FormRow>
            </FormSection>
          </>
        ) : null}

        {/* `sectiune-cap` (mud.css:4796): the heading on the left and what the
            table holds on the right, on one line, over an UNFRAMED pane. The
            three table tabs were each wrapped in a bordered, padded card that
            then contained the table's own bordered frame — two rules drawn one
            inside the other. */}
        {tab === 'data' ? (
          <section className="simsm-aggregate__pane">
            <header className="simsm-aggregate__pane-head">
              <h2>{t('aggregates.data.title')}</h2>
              <p>{t('aggregates.data.lede')}</p>
            </header>
            {/* The wireframe's `tbl zebra compact admin-aggregate-data-table`
                (AdminAgregari.tsx:1625) as AGE's own table, inside the card that
                also holds the footer (`.tbl-card`), so the count and the pager
                belong visibly to the table they page.

                An aggregate can group on six dimensions and compute eight
                results, so this table can exceed the six columns mud-table is
                published for; that is what its own `.table-scroll` is for, and
                it stays the only horizontal scroll owner. Every cell is slotted
                and kept on one line, so a code asks the table for its width
                instead of being ellipsised by mud-table's `.cell-text`.

                No QueryState empty branch: mud-table draws its own `empty` slot
                once `rows` is [], which keeps the head and the columns standing.
                Replacing the table with an info box hid what the table would
                hold — and the wireframe never does it (AdminAgregari.tsx:1651). */}
            <QueryState query={rows}>
              <div className="simsm-aggregate__table-card">
                <div className="simsm-aggregate__table">
                  <MudTable
                    className="simsm-table"
                    columns={dataColumns}
                    rows={(rowsPage?.rows ?? []).map((_row, index) => ({ id: index }))}
                    rowStyle="zebra"
                    disableSort={true}
                    aria-label={t('aggregates.data.title')}
                  >
                    <EmptyRegisterMessage>{t('aggregates.data.empty')}</EmptyRegisterMessage>
                    {(rowsPage?.rows ?? []).map((row, index) =>
                      dataColumns.map((column) => (
                        <span
                          key={`${index}-${column.key}`}
                          slot={`cell-${column.key}-${index}`}
                          className={
                            isResultColumn(column.key)
                              ? 'simsm-aggregate__cell simsm-aggregate__cell--number'
                              : 'simsm-aggregate__cell simsm-aggregate__cell--nowrap'
                          }
                        >
                          {column.key === 'bucket'
                            ? formatBucket(row[column.key], aggregate.bucketInterval)
                            : formatCell(labelOf(row, column.key) ?? row[column.key])}
                        </span>
                      )),
                    )}
                  </MudTable>
                </div>
                {totalRows > 0 ? (
                  <TableFooter
                    total={totalRows}
                    page={Math.min(page, totalPages)}
                    pageSize={rowsPageSize}
                    onPage={setPage}
                    onPageSize={setRowsPageSize}
                  />
                ) : null}
              </div>
            </QueryState>
          </section>
        ) : null}

        {tab === 'links' ? (
          <section className="simsm-aggregate__pane">
            <header className="simsm-aggregate__pane-head">
              <h2>{t('aggregates.links.title')}</h2>
              <p>{t('aggregates.links.lede')}</p>
            </header>
            {/* The wireframe's `.empty` — a 48px glyph, a title, and the line
                saying what would appear here (AdminAgregari.tsx:1751-1755). An
                info box says "here is a notice about an event"; a permanent
                "nothing uses this yet" is not an event, and EmptyState exists
                precisely so the four shapes this application had grown for it
                became one (libs/ui/shared/src/lib/empty-state.tsx). */}
            <QueryState
              query={dependencies}
              isEmpty={(dependencies.data?.length ?? 0) === 0}
              empty={
                <EmptyState
                  /* The wireframe reaches for `Icon.layers`, which AGE's
                     196-icon registry does not carry. `tree` is the nearest
                     thing it does carry and says the same thing this tab says:
                     what branches off this aggregate. */
                  icon="tree"
                  title={t('aggregates.links.empty_title')}
                  body={t('aggregates.links.empty')}
                />
              }
            >
              <DependencyTable dependencies={dependencies.data ?? []} />
            </QueryState>
          </section>
        ) : null}

        {tab === 'history' ? (
          <section className="simsm-aggregate__pane">
            <header className="simsm-aggregate__pane-head">
              <h2>{t('aggregates.history.title')}</h2>
              <p>{t('aggregates.history.lede')}</p>
            </header>
            {/* The wireframe puts the shared filter bar over this table
                (AdminAgregari.tsx:1766-1769) with the one facet its history
                takes, the period. It is the same composite the other registers
                use — the owner's 2026-08-23 ruling that the system carries one
                filter pattern — and the same preset vocabulary as the alert
                trigger history (history-period.ts). */}
            <FilterBar
              active={activePeriodFilter}
              onReset={resetPeriod}
              labels={{
                filters: t('common.filter.button'),
                reset: t('common.filter.reset'),
                panelAriaLabel: t('aggregates.history.filter.aria_label'),
                activeAriaLabel: t('common.filter.active_aria_label'),
              }}
              panel={
                <>
                  <label className="simsm-filter-bar__field">
                    <span className="simsm-filter-bar__field-label">
                      {t('alerts.filter.period')}
                    </span>
                    <MudSelect
                      size="medium"
                      value={period}
                      aria-label={t('alerts.filter.period')}
                      onMudChange={(e: CustomEvent<{ value: string }>) =>
                        setParam('period', e.detail.value)
                      }
                    >
                      <option value="">{t('alerts.filter.period.all')}</option>
                      {HISTORY_PERIODS.map((value) => (
                        <option key={value} value={value}>
                          {t(`alerts.filter.period.${value}`)}
                        </option>
                      ))}
                    </MudSelect>
                  </label>
                  {period === 'custom' ? (
                    <>
                      <label className="simsm-filter-bar__field">
                        <span className="simsm-filter-bar__field-label">
                          {t('alerts.filter.from')}
                        </span>
                        <DraftDateInput
                          placeholder={t('common.date.placeholder')}
                          size="md"
                          value={isoToDateInput(customFrom)}
                          onMudChange={(e: CustomEvent<{ value: string }>) =>
                            setParam('from', dateInputToIso(e.detail.value))
                          }
                        />
                      </label>
                      <label className="simsm-filter-bar__field">
                        <span className="simsm-filter-bar__field-label">
                          {t('alerts.filter.to')}
                        </span>
                        <DraftDateInput
                          placeholder={t('common.date.placeholder')}
                          size="md"
                          value={isoToDateInput(customTo)}
                          onMudChange={(e: CustomEvent<{ value: string }>) =>
                            setParam('to', dateInputToIso(e.detail.value))
                          }
                        />
                      </label>
                    </>
                  ) : null}
                </>
              }
            />
            <QueryState
              query={history}
              isEmpty={(history.data?.entries.length ?? 0) === 0}
              empty={
                /* Two empties, because they are two different facts: this
                   aggregate has no trail at all, or the period chosen has
                   nothing in it. Saying the first when the second is true would
                   be the screen claiming more than it knows. */
                <EmptyState
                  icon="history"
                  title={t('aggregates.history.empty_title')}
                  body={
                    activePeriodFilter.length > 0
                      ? t('aggregates.history.empty_for_period')
                      : t('aggregates.history.empty')
                  }
                />
              }
            >
              <div className="simsm-aggregate__table-card">
                <HistoryTable entries={history.data?.entries ?? []} />
                <TableFooter
                  total={historyTotal}
                  page={Math.min(historyPage, historyPages)}
                  pageSize={historyPageSize}
                  onPage={setHistoryPage}
                  onPageSize={setHistoryPageSize}
                />
              </div>
            </QueryState>
          </section>
        ) : null}
      </div>

      {dialogs}
    </section>
  );
}

// The period column, in the grain the aggregate was defined at: a day as a day,
// a month as "sept. 2026", a year as "2026". Every bucket arrives as the ISO
// date the bucket starts on; rendering it as that day made a monthly aggregate
// read as "1 sept." — a day, when the row stands for the month.
function formatBucket(value: unknown, bucketInterval: string): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return formatCell(value);
  const day = value.slice(0, 10);
  if (bucketInterval === '1 year') return day.slice(0, 4);
  if (bucketInterval === '1 month') return formatDate(day.slice(0, 7));
  if (bucketInterval === '1 week') {
    // A week is named by its first day; the reader counts seven from there.
    return formatDate(day);
  }
  return formatDate(day);
}

// docs/shape-ops-agreed-aggregations.md D7: core names the codes an aggregate
// groups by (a region, a site, a unique medicine) in each row's `_labels`.
function labelOf(row: Record<string, unknown>, key: string): string | undefined {
  const labels = row['_labels'];
  if (typeof labels !== 'object' || labels === null) return undefined;
  const label = (labels as Record<string, unknown>)[key];
  return typeof label === 'string' && label.length > 0 ? label : undefined;
}

function formatCell(value: unknown): string {
  // The em dash every other empty cell in the product uses, not a hyphen
  // (libs/ui/shared/src/lib/empty-cell.ts — declared once so the next register
  // cannot pick a third character).
  if (value === null || value === undefined || value === '') return EMPTY_CELL;
  // `Intl.NumberFormat('ro-MD')` rendered 1234.5 as "1.234,5": Romanian groups
  // thousands with a period, which is the exact glyph MUD's binding formatting
  // contract says never to use because it reads as a decimal point
  // (mud/10-data-formatting.md). `formatNumber` is that contract.
  if (typeof value === 'number') return formatNumber(value);
  // The bucket comes back as a timestamp; the period it stands for is the date.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return formatDate(value.slice(0, 10));
  }
  return String(value);
}
